import { ArrowRight, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { usePermissions } from '@/features/auth';
import { useGlobalSearch } from '@/features/global-search';

import { canSee, NAVIGATION } from '../../config/navigation';

/**
 * Ctrl/Cmd-K: jump to a page, or search orders, customers, brands and
 * providers (each only with that list's read permission, in the selected
 * country). A number offers a direct jump to that order, complaint or
 * customer. Filtering is done here, not by cmdk, because entity results are
 * already the server's matches.
 */
export function CommandPalette({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const [query, setQuery] = useState('');
  const groups = useGlobalSearch(query);

  const needle = query.trim().toLowerCase();
  const sections = NAVIGATION.map((section) => ({
    ...section,
    items: section.items.filter(
      (item) =>
        canSee(item, can) &&
        item.status === 'ready' &&
        (needle === '' ||
          `${item.title} ${item.keywords?.join(' ') ?? ''}`.toLowerCase().includes(needle)),
    ),
  })).filter((section) => section.items.length > 0);

  const go = (path: string) => {
    onOpenChange(false);
    setQuery('');
    navigate(path);
  };

  return (
    <CommandDialog
      open={open}
      onOpenChange={(value) => {
        if (!value) setQuery('');
        onOpenChange(value);
      }}
      title="Command palette"
      description="Search for a page, order, customer, brand or provider"
      shouldFilter={false}
    >
      <CommandInput
        placeholder="Jump to a page, or search orders, customers, brands…"
        value={query}
        onValueChange={setQuery}
      />
      <CommandList>
        <CommandEmpty>{needle.length >= 2 ? 'No matches.' : 'No matching pages.'}</CommandEmpty>
        {groups.map((group) => (
          <CommandGroup key={group.heading} heading={group.heading}>
            {group.isLoading && group.hits.length === 0 ? (
              <CommandItem disabled value={`${group.heading}-loading`} className="gap-2 text-xs">
                <Loader2 className="size-3.5 animate-spin" aria-hidden />
                Searching…
              </CommandItem>
            ) : (
              group.hits.map((hit) => (
                <CommandItem
                  key={`${hit.kind}-${hit.id}-${hit.title}`}
                  value={`${hit.kind}-${hit.id}-${hit.title}`}
                  onSelect={() => go(hit.to)}
                  className="gap-2 text-xs"
                >
                  <ArrowRight className="size-3.5" aria-hidden />
                  <span className="truncate">{hit.title}</span>
                  {hit.detail && (
                    <span className="text-muted-foreground ms-auto truncate">{hit.detail}</span>
                  )}
                </CommandItem>
              ))
            )}
          </CommandGroup>
        ))}
        {sections.map((section) => (
          <CommandGroup key={section.title} heading={section.title}>
            {section.items.map((item) => {
              const Icon = item.icon;
              return (
                <CommandItem
                  key={item.path}
                  value={`page-${item.path}`}
                  onSelect={() => go(item.path)}
                  className="gap-2 text-xs"
                >
                  <Icon className="size-3.5" aria-hidden />
                  {item.title}
                </CommandItem>
              );
            })}
          </CommandGroup>
        ))}
      </CommandList>
    </CommandDialog>
  );
}

/** Owns the Ctrl/Cmd-K listener so the layout does not have to. */
export function useCommandPalette() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() === 'k' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        setOpen((previous) => !previous);
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  return { open, setOpen };
}
